import { Link } from "react-router-dom";
import { 
  Building2, 
  FileText, 
  Users, 
  Receipt,
  Check,
  ArrowRight,
  ExternalLink,
  Briefcase,
  Scale
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";


const services = [
  {
    icon: Building2,
    title: "Business Formation Guidance",
    description: "Navigate the process of forming your LLC or understanding S-Corp election.",
    details: [
      "LLC formation guidance",
      "S-Corp election coordination",
      "EIN application assistance",
      "State registration guidance",
      "Operating agreement considerations"
    ],
    note: "We provide guidance and coordination; legal filings should be done through an attorney or registered agent."
  },
  {
    icon: Receipt,
    title: "Sales Tax Support",
    description: "Stay compliant with sales tax filing requirements in applicable jurisdictions.",
    details: [
      "Sales tax registration guidance",
      "Periodic filing preparation",
      "Nexus determination support",
      "Rate tracking and compliance",
      "Audit support"
    ],
    note: "Support provided for jurisdictions where you have filing obligations."
  },
  {
    icon: Users,
    title: "Payroll Coordination",
    description: "Support for payroll processing and compliance.",
    details: [
      "Payroll provider setup guidance",
      "Payroll reconciliation",
      "Quarterly payroll tax review",
      "W-2 / 1099 preparation support",
      "Compliance monitoring"
    ],
    note: "We coordinate with payroll providers; we don't process payroll directly."
  },
  {
    icon: FileText,
    title: "Year-End 1099 Support",
    description: "Ensure contractors and vendors receive proper tax documentation.",
    details: [
      "1099 eligibility review",
      "Vendor information collection",
      "1099-NEC / 1099-MISC preparation",
      "E-filing to IRS",
      "Copy distribution to recipients"
    ],
    note: "Annual service typically completed by January 31st filing deadline."
  }
];

const benefits = [
  {
    icon: Scale,
    title: "Stay Compliant",
    description: "Avoid penalties and stay on top of filing requirements."
  },
  {
    icon: Briefcase,
    title: "Systems + Structure",
    description: "Build business processes that scale with your growth."
  },
  {
    icon: FileText,
    title: "Clean Books",
    description: "Maintain organized records for tax time and decision-making."
  }
];

export default function Business() {
  return (
    <div data-testid="business-page">
      {/* Hero */}
      <section className="bg-navy text-white noise-overlay py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="font-mono text-bronze text-sm mb-4 tracking-wider uppercase">
              Business Services
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Build Your Business on a Solid Foundation
            </h1>
            <p className="font-body text-lg md:text-xl text-gray-300 mb-8">
              From entity formation to payroll support, we help small businesses establish systems, stay compliant, and maintain clean books.
            </p>
            <Button
              asChild
              className="bg-bronze hover:bg-bronze/90 text-navy rounded-full px-8 py-6 text-lg font-medium"
              data-testid="business-cta-btn"
            >
              <a href="#book" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent("open-booking")); }}>
                Book Consultation
                <ExternalLink className="ml-2 w-5 h-5" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Benefits Bar */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={benefit.title} className="flex items-center gap-4" data-testid={`benefit-${index}`}>
                <div className="w-14 h-14 rounded-full bg-bone flex items-center justify-center flex-shrink-0">
                  <benefit.icon className="w-7 h-7 text-bronze" />
                </div>
                <div>
                  <h3 className="font-body font-semibold text-navy">{benefit.title}</h3>
                  <p className="font-body text-gray-600 text-sm">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="bg-bone py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-mono text-bronze text-sm mb-3 tracking-wider uppercase">Our Services</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy mb-4">
              Business Support Services
            </h2>
            <p className="font-body text-gray-600 max-w-2xl mx-auto">
              Essential services to help your business operate smoothly and stay compliant.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <Card 
                key={service.title} 
                className="bg-white border-0 shadow-lg"
                data-testid={`business-service-${index}`}
              >
                <CardHeader>
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-xl bg-bone flex items-center justify-center flex-shrink-0">
                      <service.icon className="w-7 h-7 text-bronze" />
                    </div>
                    <div>
                      <CardTitle className="font-heading text-xl text-navy mb-2">
                        {service.title}
                      </CardTitle>
                      <p className="font-body text-gray-600">
                        {service.description}
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 mb-4">
                    {service.details.map((detail, i) => (
                      <li key={i} className="flex items-start gap-2 font-body text-sm text-gray-700">
                        <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                  {service.note && (
                    <p className="font-body text-xs text-gray-500 italic pt-4 border-t border-gray-100">
                      {service.note}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="font-mono text-bronze text-sm mb-4 tracking-wider uppercase">Our Approach</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy mb-6">
                Systems + Compliance + Clean Books
              </h2>
              <p className="font-body text-gray-600 mb-8">
                We believe every successful business needs three things: efficient systems, ongoing compliance, and accurate financial records. Our business services are designed to help you achieve all three.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-bronze/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="font-mono text-bronze font-bold text-sm">1</span>
                  </div>
                  <div>
                    <h4 className="font-body font-semibold text-navy">Assess Your Needs</h4>
                    <p className="font-body text-gray-600 text-sm">We start with a consultation to understand your business and goals.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-bronze/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="font-mono text-bronze font-bold text-sm">2</span>
                  </div>
                  <div>
                    <h4 className="font-body font-semibold text-navy">Implement Solutions</h4>
                    <p className="font-body text-gray-600 text-sm">We guide you through setup and coordinate with necessary partners.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-bronze/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="font-mono text-bronze font-bold text-sm">3</span>
                  </div>
                  <div>
                    <h4 className="font-body font-semibold text-navy">Ongoing Support</h4>
                    <p className="font-body text-gray-600 text-sm">We provide continuous support to keep you compliant and organized.</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="bg-bone rounded-2xl p-8 lg:p-12">
              <h3 className="font-heading text-2xl font-bold text-navy mb-6">
                Ready to Structure Your Business?
              </h3>
              <p className="font-body text-gray-600 mb-8">
                Whether you're just starting out or need to get organized, we're here to help. Book a consultation to discuss your specific situation.
              </p>
              <div className="space-y-4">
                <Button
                  asChild
                  className="w-full bg-navy hover:bg-gray-800 text-white rounded-full py-6 font-medium"
                  data-testid="business-book-btn"
                >
                  <a href="#book" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent("open-booking")); }}>
                    Book Free Consultation
                    <ExternalLink className="ml-2 w-4 h-4" />
                  </a>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="w-full border-navy text-navy hover:bg-navy hover:text-white rounded-full py-6 font-medium"
                  data-testid="business-contact-btn"
                >
                  <Link to="/contact">
                    Contact Us
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy text-white noise-overlay py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
            Questions About Business Services?
          </h2>
          <p className="font-body text-gray-300 mb-8 max-w-2xl mx-auto">
            Every business is unique. Let's discuss what services make sense for your situation.
          </p>
          <Button
            asChild
            className="bg-bronze hover:bg-bronze/90 text-navy rounded-full px-8 py-6 text-lg font-medium"
            data-testid="business-cta-final"
          >
            <a href="#book" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent("open-booking")); }}>
              Schedule a Call
            </a>
          </Button>
        </div>
      </section>
    </div>
  );
}
